/**
 * Similar to UserRoleCell.tsx from
 * src/pages/OrgDashboard/UserActivityDashboard/UsersTable/UserRoleCell.tsx
 */
import {Select} from '@wandb/weave/components/Form/Select';
import {Tooltip} from '@wandb/weave/components/Tooltip';
import React, {useState} from 'react';

import {useViewer} from '../../../../state/viewer/hooks';
import {useAdminModeActive} from '../../../../util/admin';
import {globalRegistryType} from '../../common/utils';
import {useOnUserRegistryRoleChange} from './hooks';
import {RegistryMember, RegistryRole} from './types';
import {
  customSelectComponents,
  CustomSingleValue,
  getRegistryRoleOptionFromRole,
  RegistryRoleOption,
  RegistryRolePill,
} from './userRegistryRoleUtils';
import {getStringFromRole} from './utils';

type UserRegistryRoleCellProps = {
  user: RegistryMember;
  registry: globalRegistryType;
  isViewerRegistryAdmin?: boolean;
  registryRoleOptions: RegistryRoleOption[];
  roleManagementEnabled: boolean;
  enableRestrictedViewerRole: boolean;
};

export const UserRegistryRoleCell = ({
  user,
  registry,
  isViewerRegistryAdmin,
  roleManagementEnabled,
  registryRoleOptions,
  enableRestrictedViewerRole,
}: UserRegistryRoleCellProps) => {
  const viewer = useViewer();
  const isSiteAdmin = useAdminModeActive();
  const [isOpen, setIsOpen] = useState(false);
  const {onUserRegistryRoleChange, isUpdating} = useOnUserRegistryRoleChange(
    registry.entityName,
    registry.registryProjectName
  );

  const isRegistryOwner = user.id === registry.owner?.id;
  if (user.viewOnlyRegistryAccess && !enableRestrictedViewerRole) {
    return (
      <Tooltip
        trigger={
          <div>
            <RegistryRolePill
              role={user.role}
              isRegistryOwner={isRegistryOwner}
            />
          </div>
        }
        position="bottom left"
        content="This role cannot be changed as the organization role is viewer"
      />
    );
  }

  const isOwnUser = viewer?.id === user.id;
  const isViewerAdmin = isSiteAdmin || isViewerRegistryAdmin;
  const isReadOnly = !isViewerAdmin || isOwnUser;
  if (isReadOnly) {
    return (
      <RegistryRolePill
        role={user.role}
        isRegistryOwner={isRegistryOwner}
        augmentedRole={user?.augmentedRole}
      />
    );
  }

  return (
    <Select<RegistryRoleOption, false>
      aria-label="update user role"
      menuIsOpen={isOpen}
      onMenuOpen={() => setIsOpen(true)}
      onMenuClose={() => setIsOpen(false)}
      options={registryRoleOptions}
      formatOptionLabel={(option: RegistryRoleOption | null) => option?.value}
      value={getRegistryRoleOptionFromRole(
        user.role as RegistryRole,
        roleManagementEnabled
      )}
      onChange={async (selected: RegistryRoleOption | null) => {
        if (selected?.value) {
          await onUserRegistryRoleChange(
            user.id,
            registry.id,
            getStringFromRole(selected.value)
          );
        }
      }}
      components={{
        ...customSelectComponents,
        SingleValue: props => (
          <CustomSingleValue
            {...props}
            isRegistryOwner={isRegistryOwner}
            augmentedRole={user?.augmentedRole}
          />
        ),
      }}
      menuPortalTarget={document.body}
      menuPosition="fixed"
      isSearchable={false}
      isLoading={isUpdating}
      isDisabled={isUpdating}
    />
  );
};
